import { Box, Button, Group, Menu, Modal, Paper, Stack, Text, Tooltip } from '@mantine/core'
import { useDisclosure } from '@mantine/hooks'
import { notifications } from '@mantine/notifications'
import { IconAlertTriangle, IconEdit, IconMailForward, IconUserCheck, IconUsers, IconUserX } from '@tabler/icons-react'
import { keepPreviousData, useMutation, useQuery } from '@tanstack/react-query'
import type { MRT_Cell, MRT_ColumnDef, MRT_PaginationState, MRT_SortingState } from 'mantine-react-table'
import { createMRTColumnHelper } from 'mantine-react-table'
import { createContext, useContext, useState } from 'react'

import { Table, TableRowActionButton } from '@/components/Table'
import { useBillingDisabledStatus } from '@/hooks/useBillingDisabledStatus'
import { useGlobalState } from '@/hooks/useGlobalState'
import type { UserRole, UserSortBy } from '@/services/api/api.types'
import { type GetUsersResponseEntry } from '@/services/api/api.types'
import * as commonClasses from '@/styles/componentCommons.css'
import { formatDate } from '@/utils/dates'
import { useNavigateTo } from '@/utils/navigation'

import type { AllowedUserRole } from './AddUserModal'
import { AddUserModal } from './AddUserModal'
import { EditUserModal } from './EditUserModal'
import { RoleTag } from './RoleTag'

// Table Context
// --------------------------------------------------------------------------------
type TableContext = {
  changeUserStatus: (data: GetUsersResponseEntry, isDisabled: boolean) => void
  editUser: (user: GetUsersResponseEntry) => void
  reinviteUser: (user: GetUsersResponseEntry) => void
}

const TableContext = createContext<TableContext>({} as TableContext)
const useTableContext = () => useContext(TableContext)

const columnHelper = createMRTColumnHelper<GetUsersResponseEntry>()

const idToSortBy = (id: string): UserSortBy | undefined => {
  switch (id) {
    case 'name':
      return 'name'
    case 'email':
      return 'email'
    // TODO: Temporarily disabled - will be restored later
    // case 'lastLogin':
    //   return 'last_login'
    case 'createdAt':
      return 'created_at'
    case 'isUserDisabled':
      return 'is_user_disabled'
    case 'totalCreditsUsed':
      return 'total_credits_used'
    case 'lastActivityUpdatedAt':
      return 'last_activity_updated_at'
    case 'status':
      return 'status'
    default:
      return undefined
  }
}

interface UserManagementProps {
  entityName: string
  role?: UserRole
  showRoleColumn?: boolean
  canInvite?: boolean
}

export function UserManagement({ entityName, role, showRoleColumn = true, canInvite = true }: UserManagementProps) {
  const { api, uiStore } = useGlobalState()
  const navigateTo = useNavigateTo()

  const [sorting, setSorting] = useState<MRT_SortingState>([])
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(10)
  const [filter, setFilter] = useState('')

  // Determine if role should be passed to modals (only for allowed roles)
  const modalRoleProps = role && ['SME', 'User', 'Admin'].includes(role) ? { role: role as AllowedUserRole } : {}

  const [modalOpened, { open: openModal, close: closeModal }] = useDisclosure(false)
  const [editModalOpened, { open: openEditModal, close: closeEditModal }] = useDisclosure(false)
  const [paymentRequiredModalOpened, { open: openPaymentRequiredModal, close: closePaymentRequiredModal }] =
    useDisclosure(false)
  const [selectedUser, setSelectedUser] = useState<GetUsersResponseEntry | null>(null)
  const [paymentBlockReason, setPaymentBlockReason] = useState<'billing' | 'freeSeats' | null>(null)

  // Check billing status for invite restrictions
  const { isDisabledDueToBilling, ignoreBilling } = useBillingDisabledStatus()
  const { data: billingData } = useQuery({
    queryKey: ['billing', 'info'],
    queryFn: () => api.getBillingInfo(),
  })
  const billingInfo = billingData?.billingInfo

  const { data, isLoading } = useQuery({
    queryKey: ['users', 'list', page, pageSize, sorting, filter, role],
    queryFn: () => {
      const sortBy = idToSortBy(sorting[0]?.id)
      const sortDir = sorting[0]?.desc == undefined ? undefined : sorting[0]?.desc ? 'desc' : 'asc'
      return api.getUsers({ page, pageSize, sortBy, sortDir, filterBy: filter, role, isUserDisabled: false })
    },
    placeholderData: keepPreviousData,
  })

  const changeUserStatusMutation = useMutation({
    mutationFn: api.changeUserStatus,
    onMutate: () => uiStore.showLoadingOverlay(),
    onSettled: () => uiStore.hideLoadingOverlay(),
    onSuccess: () => {
      api.queryClient.invalidateQueries({ queryKey: ['users', 'list'] })
    },
  })

  function changeUserStatus(data: GetUsersResponseEntry, isDisabled: boolean) {
    changeUserStatusMutation.mutate({
      user_id: data.userId,
      email: data.email,
      is_user_disabled: isDisabled,
    })
  }

  const reinviteUserMutation = useMutation({
    mutationFn: api.reinviteUser,
    onMutate: () => uiStore.showLoadingOverlay(),
    onSettled: () => uiStore.hideLoadingOverlay(),
    onSuccess: () => {
      api.queryClient.invalidateQueries({ queryKey: ['users', 'list'] })
      notifications.show({
        title: 'Invite Sent',
        message: 'A new invitation has been sent to the user.',
        color: 'green',
        classNames: commonClasses.notificationFilled,
        position: 'bottom-left',
      })
    },
  })

  function reinviteUser(user: GetUsersResponseEntry) {
    reinviteUserMutation.mutate({
      user_details: {
        user_id: user.userId,
        email: user.email,
        sme: user.smeAccess,
        sponsor_admin: user.isSponsorAdmin,
        name: user.name ?? undefined,
      },
    })
  }

  function editUser(user: GetUsersResponseEntry) {
    setSelectedUser(user)
    openEditModal()
  }

  // Table Functionality
  // ------------------------------------------------------------
  const onPaginationChange = (updater: MRT_PaginationState | ((old: MRT_PaginationState) => MRT_PaginationState)) => {
    if (typeof updater === 'function') {
      const newState = updater({ pageIndex: page - 1, pageSize })
      setPage(newState.pageIndex + 1)
      setPageSize(newState.pageSize)
    } else {
      setPage(updater.pageIndex + 1)
      setPageSize(updater.pageSize)
    }
  }

  const onSortingChange = (updater: MRT_SortingState | ((old: MRT_SortingState) => MRT_SortingState)) => {
    if (typeof updater === 'function') {
      const newSorting = updater(sorting)
      setSorting(newSorting)
    } else {
      setSorting(updater)
    }
  }

  const onGlobalFilterChange = (updater: string | ((old: string) => string)) => {
    if (typeof updater === 'function') {
      const newFilter = updater(filter)
      setFilter(newFilter)
    } else {
      setFilter(updater)
    }
  }

  const handleClosePaymentModal = () => {
    closePaymentRequiredModal()
    setPaymentBlockReason(null)
  }

  return (
    <TableContext.Provider value={{ changeUserStatus, editUser, reinviteUser }}>
      <Paper radius="md" shadow="sm">
        <Table
          columns={getColumns(showRoleColumn, role)}
          data={data?.members ?? []}
          enableColumnActions={false}
          enableColumnFilters={false}
          enableSorting={true}
          enablePagination={true}
          enableBottomToolbar={true}
          manualPagination={true}
          manualSorting={true}
          onGlobalFilterChange={onGlobalFilterChange}
          rowCount={data?.total ?? 0}
          onPaginationChange={onPaginationChange}
          onSortingChange={onSortingChange}
          state={{
            pagination: {
              pageIndex: page - 1,
              pageSize,
            },
            sorting: sorting,
            isLoading: isLoading,
          }}
          mantinePaperProps={{
            px: 'xs',
            py: 'xl',
          }}
          mantinePaginationProps={{
            showRowsPerPage: false,
            gap: 'xl',
            size: 'xl',
            styles: {
              root: {
                '--pagination-control-fz': 'xs',
              },
            },
          }}
          entity={{ singular: entityName, plural: entityName + 's', Icon: IconUsers }}
          toolbarActionsInclude={['new']}
          toolbarActionsLabelMap={{ new: 'Invite' }}
          toolbarActionsDisabled={{ new: !canInvite }}
          onToolbarActionClick={action => {
            if (action === 'new') {
              if (ignoreBilling) {
                openModal()
              } else if (isDisabledDueToBilling) {
                setPaymentBlockReason('billing')
                openPaymentRequiredModal()
              } else if (!billingInfo?.paymentMethod && (billingInfo?.freeSeats ?? 0) <= 0) {
                setPaymentBlockReason('freeSeats')
                openPaymentRequiredModal()
              } else {
                openModal()
              }
            }
          }}
        />
      </Paper>

      <Modal
        opened={modalOpened}
        onClose={closeModal}
        centered
        size="lg"
        styles={{
          body: {
            paddingTop: '0rem',
          },
          header: {
            paddingBottom: '0rem',
          },
        }}>
        <AddUserModal entityName={entityName} closeModal={closeModal} {...modalRoleProps} />
      </Modal>

      <Modal
        opened={editModalOpened}
        onClose={closeEditModal}
        centered
        size="lg"
        styles={{
          body: {
            paddingTop: '0rem',
          },
          header: {
            paddingBottom: '0rem',
          },
        }}>
        {selectedUser && (
          <EditUserModal entityName={entityName} closeModal={closeEditModal} user={selectedUser} {...modalRoleProps} />
        )}
      </Modal>

      <Modal
        opened={paymentRequiredModalOpened}
        onClose={handleClosePaymentModal}
        centered
        size="md"
        withCloseButton={false}>
        <Stack gap="lg" py="md" align="center">
          {paymentBlockReason === 'billing' && <IconAlertTriangle size={48} color="var(--mantine-color-orange-6)" />}

          <Stack gap="xs" align="center">
            {paymentBlockReason === 'billing' && (
              <Text size="lg" fw={600} ta="center">
                Payment Method Required
              </Text>
            )}
            <Text size="sm" c="dimmed" ta="center">
              To invite additional End Users or Experts, you must add a valid payment method for your channel.
            </Text>
          </Stack>

          <Group justify="center" gap="md" mt="md">
            <Button variant="default" onClick={handleClosePaymentModal}>
              {paymentBlockReason === 'freeSeats' ? 'Stay in Free Trial' : 'Cancel'}
            </Button>
            <Button
              onClick={() => {
                handleClosePaymentModal()
                navigateTo('billing', { searchParams: { tab: 'payment' } })
              }}>
              Add Payment Method
            </Button>
          </Group>
        </Stack>
      </Modal>
    </TableContext.Provider>
  )
}

const getColumns = (showRoleColumn: boolean, role?: UserRole): MRT_ColumnDef<GetUsersResponseEntry>[] => [
  {
    header: 'Name',
    accessorKey: 'name',
    size: 150,
    Cell: ({ cell }) => {
      const value = cell.getValue<string | null>()
      return (
        <Text truncate title={value || ''} style={{ maxWidth: '100%' }}>
          {value || ''}
        </Text>
      )
    },
  },
  ...(showRoleColumn
    ? [
        {
          header: 'Role',
          grow: false,
          enableSorting: false,
          accessorFn: (row: GetUsersResponseEntry) => {
            if (row.isSwiAdmin) return 'SWI Admin'
            if (row.isSponsorAdmin) return 'Admin'
            if (row.smeAccess) return 'SME'
            return 'User'
          },
          Cell: ({ cell }: { cell: MRT_Cell<GetUsersResponseEntry> }) => (
            <div style={{ paddingLeft: 0 }}>
              <RoleTag role={cell.getValue() as UserRole} />
            </div>
          ),
        },
      ]
    : []),
  {
    header: 'Email',
    accessorKey: 'email',
    grow: true,
    minSize: 200,
    sortDescFirst: false,
    Cell: ({ cell }) => {
      const value = cell.getValue<string>()
      return (
        <Text truncate title={value} style={{ maxWidth: '100%' }}>
          {value}
        </Text>
      )
    },
  },
  {
    header: 'Messages',
    accessorKey: 'totalCreditsUsed',
    grow: false,
    size: 130,
    enableSorting: true,
    sortDescFirst: true,
    Header: () => (
      <Tooltip label="Number of messages sent" withArrow>
        <span style={{ cursor: 'help' }}>Messages</span>
      </Tooltip>
    ),
    Cell: ({ cell }) => {
      const value = cell.getValue<number>()
      return <div style={{ textAlign: 'center', width: '100%' }}>{value?.toLocaleString() ?? '0'}</div>
    },
  },
  // TODO: Temporarily hidden - will be restored later
  // {
  //   header: 'Last Login',
  //   accessorKey: 'lastLogin',
  //   enableSorting: true,
  //   sortDescFirst: false,
  //   grow: false,
  //   size: 130,
  //   Cell: ({ cell }) => {
  //     const value = cell.getValue<string | null>()
  //     return value ? (
  //       formatDate(value, { format: 'MMM D, YYYY' }) || ''
  //     ) : (
  //       <Text c="gray.6" fs="italic">
  //         Invite Sent
  //       </Text>
  //     )
  //   },
  // },
  {
    header: 'Date Added',
    accessorKey: 'createdAt',
    enableSorting: true,
    sortDescFirst: false,
    grow: false,
    size: 135,
    Cell: ({ cell }) => {
      const value = cell.getValue<string | null>()
      return value ? (
        formatDate(value, { format: 'MMM D, YYYY' }) || ''
      ) : (
        <Text c="gray.6" fs="italic">
          Unknown
        </Text>
      )
    },
  },
  {
    header: 'Status',
    accessorKey: 'status',
    grow: false,
    size: 100,
    enableSorting: true,
    sortDescFirst: false,
    Cell: ({ row }) => {
      const status = row.original.status
      switch (status) {
        case 'disabled':
          return <Text c="red">Removed</Text>
        case 'expired':
          return <Text c="gray.6">Invitation Expired</Text>
        case 'subscribed':
          return (
            <Text c="green.6" fw={500}>
              Subscribed
            </Text>
          )
        case 'invited':
          return <Text c="green.6">Invited</Text>
        case 'active':
        default:
          return (
            <Text c="green.6" fw={500}>
              Active
            </Text>
          )
      }
    },
  },
  ...(role === 'User'
    ? [
        {
          header: 'Last Active',
          accessorKey: 'lastActivityUpdatedAt',
          enableSorting: true,
          sortDescFirst: true,
          grow: false,
          size: 130,
          Cell: ({ cell }: { cell: MRT_Cell<GetUsersResponseEntry> }) => {
            const value = cell.getValue<string | null>()
            return value ? (
              formatDate(value, { format: 'MMM D, YYYY' }) || ''
            ) : (
              <Text c="gray.6" fs="italic">
                No Activity
              </Text>
            )
          },
        },
      ]
    : []),
  columnHelper.display({
    id: 'actions',
    header: '',
    size: 50,
    grow: false,
    Cell: ({ row }) => {
      const { changeUserStatus, editUser, reinviteUser } = useTableContext()
      const status = row.original.status
      const showResendInvite = status === 'expired' || status === 'invited'

      return (
        <Group gap="xs" justify="flex-end" wrap="nowrap">
          <Menu withArrow position="bottom-end">
            <Menu.Target>
              <Box>
                <TableRowActionButton action="menu" tooltipLabel="More actions" />
              </Box>
            </Menu.Target>

            <Menu.Dropdown>
              <Menu.Label>Actions:</Menu.Label>
              <Menu.Item onClick={() => editUser(row.original)} leftSection={<IconEdit />}>
                Edit
              </Menu.Item>
              {showResendInvite && (
                <Menu.Item onClick={() => reinviteUser(row.original)} leftSection={<IconMailForward />}>
                  Resend Invite
                </Menu.Item>
              )}
              {status === 'disabled' ? (
                <Menu.Item
                  color="green"
                  onClick={() => changeUserStatus(row.original, false)}
                  leftSection={<IconUserCheck />}>
                  Activate
                </Menu.Item>
              ) : (
                <Menu.Item color="red" onClick={() => changeUserStatus(row.original, true)} leftSection={<IconUserX />}>
                  Remove
                </Menu.Item>
              )}
            </Menu.Dropdown>
          </Menu>
        </Group>
      )
    },
  }),
]
